import { formatCurrency, formatPercent } from "~/lib/format";
import type { YearSummary } from "~/types/financials";
import { ChangeIndicator } from "./change-indicator";
import { Sparkline } from "./sparkline";

interface FinancialKpiCardsProps {
	years: YearSummary[];
}

interface KpiDefinition {
	label: string;
	value: number | null;
	change: number | null;
	sparkData: (number | null)[];
	format: (value: number | null) => string;
}

function changeBetween(current: number | null, prior: number | null): number | null {
	if (current == null || prior == null || prior === 0) return null;
	return (current - prior) / Math.abs(prior);
}

function KpiGrid({ kpis }: { kpis: KpiDefinition[] }) {
	const visibleKpis = kpis.filter((kpi) => kpi.value != null);
	if (visibleKpis.length === 0) return null;

	return (
		<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
			{visibleKpis.map((kpi) => {
				const hasTrend = kpi.sparkData.filter((value) => value != null).length > 1;
				return (
					<div key={kpi.label} className="card-surface rounded-xl p-4">
						<p className="label-overline">{kpi.label}</p>
						<p className="mt-1.5 font-mono text-2xl font-semibold tabular-nums">
							{kpi.format(kpi.value)}
						</p>
						{(kpi.change != null || hasTrend) && (
							<div className="mt-2 flex items-center justify-between">
								{kpi.change != null ? <ChangeIndicator value={kpi.change} /> : <span />}
								{hasTrend && <Sparkline data={kpi.sparkData} />}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}

export function BalanceSheetKpiCards({ years }: FinancialKpiCardsProps) {
	const latest = years.at(-1);
	if (!latest) return null;
	const prior = years.at(-2) ?? null;

	return (
		<KpiGrid
			kpis={[
				{
					label: `Aktiva ${latest.year}`,
					value: latest.totalAssets,
					change: changeBetween(latest.totalAssets, prior?.totalAssets ?? null),
					sparkData: years.map((year) => year.totalAssets),
					format: formatCurrency,
				},
				{
					label: `Vlastní kapitál ${latest.year}`,
					value: latest.equity,
					change: changeBetween(latest.equity, prior?.equity ?? null),
					sparkData: years.map((year) => year.equity),
					format: formatCurrency,
				},
				{
					label: `Cizí zdroje ${latest.year}`,
					value: latest.foreignResources,
					change: changeBetween(latest.foreignResources, prior?.foreignResources ?? null),
					sparkData: years.map((year) => year.foreignResources),
					format: formatCurrency,
				},
				{
					label: `Míra vlastního kapitálu ${latest.year}`,
					value: latest.ratios.equityRatio,
					change: changeBetween(latest.ratios.equityRatio, prior?.ratios.equityRatio ?? null),
					sparkData: years.map((year) => year.ratios.equityRatio),
					format: formatPercent,
				},
			]}
		/>
	);
}

export function IncomeStatementKpiCards({ years }: FinancialKpiCardsProps) {
	const latest = years.at(-1);
	if (!latest) return null;
	const prior = years.at(-2) ?? null;

	return (
		<KpiGrid
			kpis={[
				{
					label: `Tržby ${latest.year}`,
					value: latest.revenue,
					change: changeBetween(latest.revenue, prior?.revenue ?? null),
					sparkData: years.map((year) => year.revenue),
					format: formatCurrency,
				},
				{
					label: `Čistý zisk ${latest.year}`,
					value: latest.netProfit,
					change: changeBetween(latest.netProfit, prior?.netProfit ?? null),
					sparkData: years.map((year) => year.netProfit),
					format: formatCurrency,
				},
			]}
		/>
	);
}
